import { gql, useQuery } from '@apollo/client'
import { useParams } from 'react-router-dom'
import { useState, useEffect } from 'react'
import Select from 'react-select'
import { Popover, PopoverHeader, PopoverBody, Form, FormGroup, Label, Input, Button, Alert } from 'reactstrap'
import { useApolloClient } from '@apollo/client'
import "./RenderAddCustomColumn.css"
import { CUSTOM_FK_CONFIG } from "./ForeignKeyComponents"

const ColumnTypesQuery = gql`query ColumnTypesQuery {
    __type(name:"TaskRabbitColumnColumnTypeChoices") {
        enumValues {
            name
            description
        }
    }
}`
const ColumnElementTypesQuery = gql`query ColumnElementTypesQuery {
    __type(name:"TaskRabbitColumnElementTypeChoices") {
        enumValues {
            name
            description
        }
    }
}`
const ContentTypesQuery = gql`query ContentTypesQuery($modelsIn: [String!]!) {
    allContentTypes(modelsIn: $modelsIn) {
        edges {
            node {
                id
                appLabel
                model
            }
        }
    }
}`

const CreateBoardColumnMutation = gql`mutation CreateBoardColumnMutation($input: CreateBoardColumnMutationInput!){
    createBoardColumn(input: $input){
        ok
        errors{
            field
            messages
        }
    }
}`

const ALLOWED_CONTENT_TYPES = Object.keys(CUSTOM_FK_CONFIG)

const RenderAddCustomColumn = ({ refetchQuery }) => {
    const apolloClient = useApolloClient()
    const boardId = useParams().boardId
    const [openModal, setOpenModal] = useState("")
    const [error, setError] = useState('')
    const [loading, setLoading] = useState(false)
    const [formData, setFormData] = useState({
        boardId: boardId,
        title: '',
        columnType: 'STRING',
        elementType: 'STRING',
        isRequired: false,
        contentType: null,
        allowedOptions: [],
        stateTransitions: null,
    })
    const { data: columnTypesData } = useQuery(ColumnTypesQuery)
    const { data: columnElementTypesData } = useQuery(ColumnElementTypesQuery)
    const { data: contentTypesData } = useQuery(ContentTypesQuery, {
        variables: {
            modelsIn: ALLOWED_CONTENT_TYPES
        }
    })

    useEffect(() => {
        if (openModal === "") {
            setFormData({
                boardId: boardId,
                title: '',
                columnType: 'STRING',
                elementType: 'STRING',
                isRequired: false,
                contentType: null,
                allowedOptions: [],
                stateTransitions: null,
            })
            setError("")
        }
    }, [openModal, boardId])

    const handleChange = (e) => {
        if (e.target.name === "isRequired") {
            setFormData({ ...formData, [e.target.name]: e.target.checked })
        } else {
            setFormData({ ...formData, [e.target.name]: e.target.value })
        }
    }
    const handleSelectChange = (name, value) => {
        if (name === "columnType" && !["LIST", "FK_OBJECT", "ENUM"].includes(value)) {
            setFormData({ ...formData, [name]: value, "elementType": value })
        } else if (name === "columnType" && "FK_OBJECT" === value) {
            setFormData({ ...formData, [name]: value, "elementType": "NUMBER" })
        } else {
            setFormData({ ...formData, [name]: value })
        }
    }

    const handleAddEnumOption = () => {
        setFormData({ ...formData, allowedOptions: [...(formData.allowedOptions || []), ''] })
    }

    const handleEnumOptionChange = (index, value) => {
        const updated = [...(formData.allowedOptions || [])]
        updated[index] = value
        setFormData({ ...formData, allowedOptions: updated })
    }

    const handleRemoveEnumOption = (index) => {
        const updated = (formData.allowedOptions || []).filter((_, i) => i !== index)
        setFormData({ ...formData, allowedOptions: updated })
    }
    const handleSubmit = (e) => {
        e.preventDefault()
        if (loading) return
        setLoading(true)
        try {
            const { title, columnType, allowedOptions, stateTransitions, ...baseInput } = formData
            if (!title) {
                setError("Title is required")
                setLoading(false)
                return
            } else if (!columnType) {
                setError("Column type is required")
                setLoading(false)
                return
            }
            const input = {
                title,
                columnType,
                ...baseInput,
                ...(columnType === "ENUM" ? { allowedOptions, stateTransitions } : {}),
            }
            apolloClient.mutate({
                mutation: CreateBoardColumnMutation,
                variables: { input },
            }).then((result) => {
                if (result?.data?.createBoardColumn?.ok) {
                    setError("")
                    refetchQuery()
                    setOpenModal("")
                } else if (result?.data?.createBoardColumn?.errors) {
                    setError(result.data.createBoardColumn.errors[0].messages[0])
                }
            }).catch((error) => {
                setError("An error occurred, could not complete request.")
            }).finally(() => {
                setLoading(false)
            })
        } catch (error) {
            setError("An error occurred, could not complete request.")
            setLoading(false)
        }
    }
    return (
        <>
            <i id="addCustomColumn" className="fa fa-plus-square-o add-custom-column-icon" aria-hidden="true" onClick={() => setOpenModal("addCustomColumn")} />
            <Popover trigger="legacy" placement="left" isOpen={openModal === "addCustomColumn"} target={"addCustomColumn"} toggle={() => setOpenModal("")} className="AddCustomColumnPopOver">
                <PopoverHeader><i className="fa fa-plus" /> Add Custom Column</PopoverHeader>
                <PopoverBody>
                    {error && <Alert color="danger">{error}</Alert>}
                    <Form onSubmit={handleSubmit} id="addCustomColumnForm" className="addCustomColumnForm">
                        <FormGroup>
                            <Label for="title">Title</Label>
                            <Input type="text" name="title" id="title" value={formData.title} onChange={handleChange} />
                        </FormGroup>
                        <FormGroup>
                            <Label for="columnType">Type</Label>
                            <Select options={columnTypesData?.__type?.enumValues?.map((columnType) => ({ value: columnType.name, label: columnType.description }))} name="columnType" id="columnType" value={{ value: formData.columnType, label: columnTypesData?.__type?.enumValues?.find((columnType) => columnType.name === formData.columnType)?.description }} onChange={(value) => handleSelectChange("columnType", value.value)} />
                        </FormGroup>
                        {"LIST" === formData.columnType && (
                            <FormGroup>
                                <Label for="elementType">Element Type</Label>
                                <Select options={columnElementTypesData?.__type?.enumValues?.map((columnElementType) => ({ value: columnElementType.name, label: columnElementType.description }))} name="elementType" id="elementType" onChange={(value) => handleSelectChange("elementType", value.value)} />
                            </FormGroup>
                        )}
                        {"FK_OBJECT" === formData.columnType && <FormGroup>
                            <Label for="contentType">Content Type</Label>
                            <Select options={contentTypesData?.allContentTypes?.edges?.map((contentType) => ({ value: contentType.node.id, label: contentType.node.appLabel.toUpperCase() + ' - ' + contentType.node.model.toUpperCase() }))} name="contentType" id="contentType" onChange={(value) => handleSelectChange("contentType", value.value)} />
                        </FormGroup>}
                        {"ENUM" === formData.columnType && (
                            <FormGroup>
                                <Label>Options</Label>
                                {(formData.allowedOptions || []).map((opt, index) => (
                                    <div key={index} className="input-group mb-1">
                                        <Input
                                            type="text"
                                            value={opt}
                                            onChange={(e) => handleEnumOptionChange(index, e.target.value)}
                                            placeholder={`Option ${index + 1}`}
                                        />
                                        <Button color="danger" size="sm" onClick={() => handleRemoveEnumOption(index)}>×</Button>
                                    </div>
                                ))}
                                <Button color="secondary" size="sm" type="button" onClick={handleAddEnumOption} style={{ width: '100%' }}>+ Add Option</Button>
                            </FormGroup>
                        )}
                        <FormGroup check>
                            <Input type="checkbox" name="isRequired" id="isRequired" checked={formData.isRequired} onChange={handleChange} />
                            <Label for="isRequired">Is Required</Label>
                        </FormGroup>
                        <Button color="primary" type="submit" form="addCustomColumnForm" disabled={loading}>Add Column</Button>
                    </Form>
                </PopoverBody>
            </Popover>
        </>
    )
}

export default RenderAddCustomColumn